import { useEffect } from 'react'
import { message, theme } from 'antd'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { HorizontalContainer, LogoLoading, PageError, StaticPageError, VerticalContainer } from 'components/styled'
import { getLocale, getShopId, getStorageLocationId, getIsHardLoggingOut, hardLogout, queryClient, updateAdminVersion, updateLocale, updateLocationId, useAuthentication, useTranslate, useWindowDimensions } from 'utils'
import { useConfig, useCurrentLocation, useCurrentShop, useMe, useProfileUpdate } from 'api'
import { SideMenu } from 'components/layout/side-menu/side-menu'
import { BottomMenu } from 'components/layout/bottom-menu/bottom-menu'
import { LayoutHeader } from 'components/layout/layout-header/layout-header'
import { Me } from 'types'

export const Layout = () => {

    const { isLoggedIn, logout } = useAuthentication()

    useEffect(() => {
        if(!isLoggedIn && !getIsHardLoggingOut()){
            logout()
        }
    }, [isLoggedIn])

    return isLoggedIn ? <LayoutLocaleWrapper/> : <></>

}

const LayoutLocaleWrapper = () => {
    
    const { data: me, isLoading: isMeLoading } = useMe()
    const locale = getLocale()

    return (
        isMeLoading ? <LogoLoading/> :
        !me ? <StaticPageError/> :
        (locale && locale !== me.locale) ? <LayoutLocaleUpdater me={me} locale={locale}/> :
        <LayoutConfigWrapper/>
    )

}

const LayoutLocaleUpdater = ({ me, locale }: { me: Me, locale: string }) => {
    
    const { __ } = useTranslate()
    const { mutate: updateUser, isLoading, data: mutationResponse } = useProfileUpdate({...me, locale})

    useEffect(() => {
        updateUser()
    }, [])

    useEffect(() => {
        if(mutationResponse === 'locale_is_not_supported'){
            message.error(__('locale_is_not_supported', { locale }))
            updateLocale(me.locale)
        }else if(mutationResponse){
            const shopId = getShopId()
            queryClient.resetQueries(['config', shopId])
            queryClient.resetQueries(['me'])
        }
    }, [mutationResponse])

    return (
        isLoading ? <LogoLoading/> :
        !mutationResponse ? <LayoutConfigWrapper/> :
        <LayoutConfigWrapper/>
    )
    
}

const LayoutConfigWrapper = () => {

    const { data: config, isLoading: isConfigLoading } = useConfig()

    useEffect(() => { // Admin version
        if(config && !config.isMigratedToMRML){
            updateAdminVersion('v1')
        }
    }, [config])

    return (
        isConfigLoading ? <LogoLoading/> :
        !config ? <StaticPageError/> :
        !config.isMigratedToMRML ? <LogoLoading/> :
        <LayoutShopWrapper/>
    )
}

const LayoutShopWrapper = () => {
    const { data: shop, isLoading: isShopLoading } = useCurrentShop()

    useEffect(() => {
        if(shop === null) hardLogout()
    }, [shop])

    return (
        isShopLoading ? <LogoLoading/> :
        !shop ? <PageError/> :
        <LayoutLocationWrapper/>
    )
}

const LayoutLocationWrapper = () => {

    //hooks:
    const windowLocation = useLocation()
    const navigate = useNavigate()

    //api:
    const { data: currentLocation, isLoading: isCurrentLocationLoading } = useCurrentLocation()

    useEffect(() => {
        if(
            !windowLocation.pathname.includes('/appointments') &&
            getStorageLocationId() === 'all'
        ){
            updateLocationId(null)
            queryClient.removeQueries(['currentLocation'])
            navigate(location.pathname)
        }
    }, [windowLocation.pathname])
    
    return (
        isCurrentLocationLoading ? <LogoLoading/> :
        (currentLocation === false || currentLocation === undefined) ? <PageError/> :
        <LayoutContent/>
    )
    
}

const LayoutContent = () => {

    //hooks:
    const { token: { colorBgLayout, colorBgContainer, paddingXXS, margin }} = theme.useToken()
    const { SM, LG } = useWindowDimensions()

    return (
        <VerticalContainer style={{
            width: '100%',
            height: '100%',
            background: SM ? colorBgLayout : colorBgContainer,
            gap: 0
        }}>

            <VerticalContainer id='layoutScrollContainer' style={{ flex: 1, overflowY: 'auto', gap: 0 }}>

                {/* header */}
                {LG && <LayoutHeader/>}

                <VerticalContainer style={{
                    flex: 1,
                    justifyContent: 'space-between'
                }}>
                
                    <HorizontalContainer style={{
                        flex: 1,
                        padding: LG ? `0 ${paddingXXS}px` : 0,
                        alignItems: 'stretch',
                        gap: margin
                    }}>

                        {/* side menu */}
                        {LG && (<div><SideMenu/></div>)}

                        {/* outlet */}
                        <div style={{ maxWidth: LG ? 'calc(100% - 243px - 8px)' : '100%', flex: 1, display: 'flex' }}>
                            <Outlet/>
                        </div>

                    </HorizontalContainer>

                </VerticalContainer>

            </VerticalContainer>

            {/* bottom menu */}
            {!LG && <BottomMenu/>}

        </VerticalContainer>
    )

}
